import api from '@/utils/api'
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { LocalNotifications } from '@capacitor/local-notifications'
import router from '@/router/router'

// Push notifications for the team and player picked on the profile (FCM topics "team-<id>" / "player-<id>"),
// and the news (topic "news"), followed by every device that follows a team or a player.
// Web: Firebase JS SDK (loaded from the CDN) + backend endpoint that subscribes the token to the topics.
// App: @capacitor-firebase/messaging, which subscribes to the topics directly on the device.

const FIREBASE_SDK = 'https://www.gstatic.com/firebasejs/10.12.2'
const TOKEN_KEY = 'big-push-token'

let messagingPromise = null
let nativeListening = false

const isNative = () => Capacitor.isNativePlatform()

const loadScript = src => new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = resolve
    script.onerror = () => reject(new Error(`Could not load ${src}`))
    document.head.appendChild(script)
})

const webConfig = () => window.BIG_FIREBASE_CONFIG

const getWebMessaging = () => {
    if (!messagingPromise) {
        messagingPromise = loadScript('firebase-config.js')
            .then(() => loadScript(`${FIREBASE_SDK}/firebase-app-compat.js`))
            .then(() => loadScript(`${FIREBASE_SDK}/firebase-messaging-compat.js`))
            .then(() => {
                // eslint-disable-next-line no-unused-vars
                const { vapidKey, ...firebaseConfig } = webConfig()
                if (!firebaseConfig.apiKey) {
                    throw new Error('Firebase is not configured (public/firebase-config.js)')
                }
                if (!window.firebase.apps.length) {
                    window.firebase.initializeApp(firebaseConfig)
                }
                const messaging = window.firebase.messaging()
                // while the site is open FCM does not show the notification, so show it ourselves
                messaging.onMessage(payload => {
                    navigator.serviceWorker.ready.then(registration => {
                        registration.showNotification(payload.notification?.title || 'BIG Basketball', {
                            body: payload.notification?.body,
                            icon: 'icon-192.png',
                            data: { FCM_MSG: { data: payload.data } }
                        })
                    })
                })
                return messaging
            })
            .catch(error => {
                messagingPromise = null
                throw error
            })
    }
    return messagingPromise
}

// Same targets as the notificationclick handler in public/firebase-messaging-sw.js
const NEWS_TOPIC = 'news'

const openNotificationTarget = (data = {}) => {
    if (data.articleId) {
        router.push({ name: 'Article', params: { id: data.articleId } }).catch(() => {})
    } else if (data.matchId) {
        router.push({ name: 'Match', params: { id: data.matchId } }).catch(() => {})
    } else if (data.playerId) {
        router.push({ name: 'Player', params: { id: data.playerId } }).catch(() => {})
    }
}

// On iOS the FCM token only exists once Apple has handed the app its APNs token, which arrives
// asynchronously after launch; topic (un)subscriptions fail until then, so wait for it.
const waitForNativeToken = async (attempts = 10) => {
    let lastError
    for (let i = 0; i < attempts; i++) {
        try {
            const { token } = await FirebaseMessaging.getToken()
            if (token) {
                return token
            }
        } catch (error) {
            lastError = error
        }
        await new Promise(resolve => setTimeout(resolve, 1000))
    }
    throw lastError || new Error('No push token')
}

const listenNative = () => {
    if (nativeListening) {
        return
    }
    nativeListening = true
    FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
        openNotificationTarget(notification.data)
    })
    // iOS shows notifications in the foreground itself (presentationOptions in capacitor.config.json),
    // Android hands them to the app instead, so show them as local notifications
    if (Capacitor.getPlatform() === 'android') {
        FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
            LocalNotifications.schedule({
                notifications: [{
                    id: Math.floor(Math.random() * 2147483647),
                    title: notification.title || 'BIG Basketball',
                    body: notification.body || '',
                    extra: notification.data
                }]
            })
        })
        LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) => {
            openNotificationTarget(notification.extra)
        })
    }
}

const getWebToken = async () => {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
        throw new Error('permission-denied')
    }
    const messaging = await getWebMessaging()
    const registration = await navigator.serviceWorker.register('firebase-messaging-sw.js')
    const token = await messaging.getToken({ vapidKey: webConfig().vapidKey, serviceWorkerRegistration: registration })
    localStorage.setItem(TOKEN_KEY, token)
    return token
}

export default {
    isSupported() {
        if (isNative()) {
            return true
        }
        return 'serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window
    },

    // Call on app start: while a tab of the site is visible FCM hands messages to the page instead of
    // showing them, so the page must be listening or those notifications are lost.
    init() {
        if (isNative()) {
            listenNative()
            return
        }
        if (!this.isSupported() || Notification.permission !== 'granted') {
            return
        }
        getWebMessaging().catch(error => console.error('Could not start push notifications', error))
    },

    isDenied() {
        return !isNative() && 'Notification' in window && Notification.permission === 'denied'
    },

    // Topics a device follows for the team / player picked on the profile, plus the news with either of them.
    topicsFor(team, player) {
        const topics = [team && `team-${team.id}`, player && `player-${player.id}`].filter(Boolean)
        return topics.length ? topics.concat(NEWS_TOPIC) : topics
    },

    // Starts following `subscribe` topics and stops following `unsubscribe` topics.
    // Only asks for notification permission when there is something to subscribe to.
    async updateTopics(subscribe = [], unsubscribe = []) {
        unsubscribe = unsubscribe.filter(topic => !subscribe.includes(topic))
        if (!subscribe.length && !unsubscribe.length) {
            return
        }
        if (isNative()) {
            if (subscribe.length) {
                const { receive } = await FirebaseMessaging.requestPermissions()
                if (receive !== 'granted') {
                    throw new Error('permission-denied')
                }
                listenNative()
                await waitForNativeToken()
            } else if (!await waitForNativeToken().then(() => true, () => false)) {
                // Never registered with FCM, so there is nothing to unsubscribe from
                return
            }
            for (const topic of unsubscribe) {
                await FirebaseMessaging.unsubscribeFromTopic({ topic })
            }
            for (const topic of subscribe) {
                await FirebaseMessaging.subscribeToTopic({ topic })
            }
            return
        }
        const token = subscribe.length ? await getWebToken() : localStorage.getItem(TOKEN_KEY)
        if (token) {
            await api.post('notifications/subscription', { token, subscribe, unsubscribe })
        }
    }
}
